/*
 * INT — Human Sign-off / Approved Archive figure.
 * Geometry + step queue ported 1:1 from INT_Approval_Gate v1 / approval.js.
 * Uses the single shared Hairline kernel (src/components/feature/hairline/kernel.ts).
 * Hairline — https://github.com/lucasmarkes/hairline (commit bc782244), MIT License,
 * Copyright (c) Lucas Markes. Full license text is kept in the kernel file header.
 * Invariants kept: approval only via an explicit setState('approved'); signing only happens
 * while the sheet rests on the review desk; returns from the archive stop at the desk first.
 * Pointer preview is disabled (stage carries data-controlled) — no hover approval path exists.
 */
/* eslint-disable */
import {
  Cam, fit, proj, facing, rings, prism, solid, put, mk, poly, fillet, seg, lerp,
  tween, tset, tval, tdone, register, pointer, disposer,
} from '@/components/feature/hairline/kernel';

export type IntState = 'pending' | 'review' | 'approved' | 'changes';

export interface IntHandle {
  set: (v: number) => void;
  setState: (next: IntState) => void;
  onState: (fn: (s: IntState) => void) => void;
  destroy: () => void;
}

interface Ctx { stage: Element; svg: SVGSVGElement; read: { textContent: string } }

export function mountApproval({ stage, svg, read }: Ctx, value: number): IntHandle {
  const bag = disposer(), C = Cam(45, .5, 1.65), world = (u, v, z) => [(u + v) / Math.sqrt(2), (v - u) / Math.sqrt(2), z];
  fit(C, [world(-114, -60, 0), world(114, 80, 0), world(-114, -60, 40), world(114, 80, 40)], 200, 150);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), fixed = mk('g', {}, g), moving = mk('g', {}, g), foreground = mk('g', {}, g);
  let position = tween(0), opening = tween(0);
  let state = 'pending', reported = 'pending', onState: (s: any) => void = () => {}, last = '', strength = value, queue: any[] = [], action: any = null, signed = false;
  function box(parent, x, y, w, d, z, h, r = 2) { const e = solid(parent), [a, b] = rings(x, y, x + w, y + d, r, .6); put(e, prism(P, front, a, b, z, z + h)); return e; }
  function record(parent, u, v, z, active = false, isSigned = false) {
    const [x, y] = world(u, v, 0), e = box(parent, x - 15, y - 20, 30, 40, z, .8, 2); e.sil.classList.toggle('hi', active);
    mk('path', { class: 'nf lo', d: [-11, -4, 3].map((dy, i) => seg(P(x - 9, y + dy, z + .8), P(x + 9 - i * 2, y + dy, z + .8))).join('') }, parent);
    if (isSigned) { const pts = [[-9, 12], [-6, 8], [-4, 13], [0, 9], [2, 12], [8, 12]].map(([a, b]) => P(x + a, y + b, z + .9)); mk('path', { class: 'nf hi', d: pts.map((pt, i) => (i ? 'L' : 'M') + pt.join(',')).join('') }, parent); }
  }
  // Paper request stack, physical signature clipboard, and one tabbed archive folder.
  for (let i = 0; i < 2; i++) record(fixed, -84 + i, 0, 1 + i * 1.2);
  const [cx, cy] = world(0, 0, 0); box(fixed, cx - 22, cy - 28, 44, 56, 0, 2, 3);
  const [fx, fy] = world(84, 0, 0), back = [[-23, -28], [-20, -28], [-20, -34], [-3, -34], [0, -28], [23, -28], [23, 28], [-23, 28]].map(([x, y]) => P(fx + x, fy + y, 1.5));
  mk('path', { class: 'sil', d: poly(fillet(back, [2, 1, 2, 2, 1, 2, 2, 2], 4)) }, fixed);
  function draw(p, o) {
    moving.replaceChildren(); foreground.replaceChildren();
    const u = p <= 1 ? lerp(-84, 0, p) : lerp(0, 84, p - 1), z = 6 + Math.sin(Math.PI * (p % 1)) * 7 * strength;
    record(moving, u, 0, z, true, signed || (action?.kind === 'sign' && o > .75));
    // A real clip: thin jaw with an open spring handle.
    box(foreground, cx - 10, cy - 25, 20, 6, 7, 1, 1.5);
    const clip = [P(cx - 8, cy - 25, 8), P(cx - 8, cy - 29, 12), P(cx + 8, cy - 29, 12), P(cx + 8, cy - 25, 8)]; mk('path', { class: 'nf sil', d: poly(fillet(clip, [1, 1, 1, 1], 5)) }, foreground);
    // Pen rests beside the sheet, moves to the signature line only on explicit approval.
    const beat = action?.kind === 'sign' ? Math.sin(Math.PI * o) : 0, px = cx + lerp(28, 7, beat), py = cy + lerp(-9, 14, beat), pz = lerp(3, 8, beat);
    box(foreground, px - 1.2, py - 10, 2.4, 22, pz, 1.5, 1);
    mk('path', { class: 'sil', d: poly(fillet([P(px - 1.2, py + 12, pz), P(px + 1.2, py + 12, pz), P(px, py + 16, pz)], [.4, .4, .2], 4)) }, foreground);
    mk('path', { class: 'nf lo', d: seg(P(px - 1, py - 5, pz + 1.5), P(px + 1, py - 5, pz + 1.5)) }, foreground);
    const cover = [P(fx - 23, fy - 8, 11), P(fx + 23, fy - 8, 11), P(fx + 23, fy + 28, 2), P(fx - 23, fy + 28, 2)]; mk('path', { class: 'sil', d: poly(fillet(cover, [2, 2, 2, 2], 4)) }, foreground);
    mk('path', { class: 'nf lo', d: seg(P(fx - 19, fy + 25, 3), P(fx + 19, fy + 25, 3)) }, foreground);
    // Approved sheets carry a clear check on the visible upper portion.
    if (p > 1.99 && signed) { mk('path', { class: 'nf hi', d: seg(P(fx - 5, fy - 13, 7), P(fx - 2, fy - 10, 7)) + seg(P(fx - 2, fy - 10, 7), P(fx + 4, fy - 17, 7)) }, moving); }
  }
  function start(now) {
    action = queue.shift() || null; if (!action) return;
    if (action.kind === 'move') tset(position, action.to, now, 0);
    else { opening = tween(0); tset(opening, 1, now, 0); }
  }
  let B: any = null;
  function choose(next) {
    if (!['pending', 'review', 'approved', 'changes'].includes(next) || (next === state && action)) return;
    const now = performance.now(), p = tval(position, now); state = next; queue = []; action = null;
    if (next === 'approved') {
      if (p < 1.999 || !signed) { if (Math.abs(p - 1) > .001) queue.push({ kind: 'move', to: 1 }); queue.push({ kind: 'sign' }, { kind: 'move', to: 2 }); }
    } else {
      const end = next === 'review' ? 1 : 0;
      // All returns from the archive stop at the review desk before continuing left.
      if (p > 1.001) queue.push({ kind: 'move', to: 1, clear: true }, { kind: 'pause' });
      else if (Math.abs(p - 1) < .001) signed = false;
      if (Math.abs(p - end) > .001 && (end !== 1 || p <= 1.001)) queue.push({ kind: 'move', to: end });
    }
    // Freeze interrupted movement until its replacement step is scheduled.
    position = tween(p); opening = tween(0); start(now); last = ''; B?.wake();
  }
  draw(0, 0); read.textContent = 'pending';
  B = register(stage, (_dt, now) => {
    if (action && tdone(action.kind === 'move' ? position : opening, now)) {
      if (action.kind === 'sign') signed = true; if (action.clear) signed = false; start(now);
    }
    const p = tval(position, now), o = tval(opening, now), key = [p, o, state, strength, signed, action?.kind].join('|'); if (key !== last) { draw(p, o); last = key; }
    /* While any step is running the trace stays at pending/review; "recorded" only after arrival. */
    const actual = action ? (p < .99 ? 'pending' : 'review') : state;
    if (actual !== reported) { reported = actual; read.textContent = actual; onState(actual); }
    return !!action;
  }); bag.add(B.unregister);
  // Pointer can preview review only. Approval is an explicit external action.
  if (!(stage as Element).hasAttribute?.('data-controlled')) bag.add(pointer(stage, { move: () => choose('review'), leave: () => choose('pending') }));
  bag.add(() => svg.replaceChildren());
  return {
    set: (v) => { strength = v; last = ''; B.wake(); },
    setState: choose,
    onState: (fn) => { onState = fn; fn(reported as IntState); },
    destroy: bag.dispose,
  };
}